export interface GoalItemType {
  goalName: string;
  goalDescription: string;
  targets: TargetItem[];
}

export interface TargetItem {
  targetName: string;
  targetDescription: string;
}

/**
 * Creates a GoalItemType with default values for any missing properties.
 * Ensures all required fields have at least empty string values.
 * @param input - Partial goal data from server or user input
 * @returns Complete GoalItemType with defaults for missing fields
 */
export const goalWithDefaults = (
  input: Omit<Partial<GoalItemType>, "targets"> & { targets?: Partial<TargetItem>[] },
): GoalItemType => ({
  goalName: input.goalName || "",
  goalDescription: input.goalDescription || "",
  targets: (input.targets || []).map(targetWithDefaults),
});

/**
 * Creates a TargetItem with default values for any missing properties.
 * Ensures all required fields have at least empty string values.
 * @param target - Partial target data from server or user input
 * @returns Complete TargetItem with defaults for missing fields
 */
export const targetWithDefaults = (target: Partial<TargetItem>): TargetItem => ({
  targetName: target.targetName || "",
  targetDescription: target.targetDescription || "",
});

interface AIparsedTreatmentPlanDataResponse {
  goals?: {
    goal_name?: string;
    goal_description?: string;
    targets?: {
      target_name?: string;
      target_description?: string;
    }[];
  }[];
}

/**
 * Parses raw JSON string from AI treatment plan parsing service into structured GoalItemType array.
 * Transforms snake_case API response fields to camelCase GoalItemType properties.
 * Handles missing or malformed data gracefully by returning null.
 * @param serverData - Raw JSON string containing AI-parsed treatment plan data
 * @returns Array of GoalItemType with defaults applied, or null if parsing fails or data is invalid
 */
export const rawAIparsedTreatmentPlanDataToGoals = (serverData: unknown): GoalItemType[] | null => {
  if (!serverData) return null;
  if (typeof serverData !== "string") return null;
  try {
    const data = JSON.parse(serverData) as AIparsedTreatmentPlanDataResponse;
    if (!data || !data?.goals || !Array.isArray(data.goals)) return null;
    const goals: GoalItemType[] = data.goals.map((g) =>
      goalWithDefaults({
        goalName: g?.goal_name,
        goalDescription: g?.goal_description,
        targets: g?.targets?.map((t) =>
          targetWithDefaults({
            targetName: t?.target_name,
            targetDescription: t?.target_description,
          }),
        ),
      }),
    );
    return goals;
  } catch (error) {
    console.error("Failed to parse treatment plan data", { error, serverData });
    return null;
  }
};

/**
 * Performs a deep equality check between two arrays of goals.
 * Compares all properties of each goal including nested targets.
 * Used to detect if there are unsaved changes when user attempts to close the dialog.
 * @param goals1 - First array of goals to compare
 * @param goals2 - Second array of goals to compare
 * @returns true if all goals and their nested targets are equal, false otherwise
 */
export const areGoalsEqual = (goals1: GoalItemType[], goals2: GoalItemType[]): boolean => {
  if (goals1 === goals2) return true;
  if (goals1.length !== goals2.length) return false;

  return goals1.every((goal1, i) => {
    const goal2 = goals2[i];
    if (!goal2) return false;

    if (goal1.goalName !== goal2.goalName || goal1.goalDescription !== goal2.goalDescription) {
      return false;
    }

    return areTargetsEqual(goal1.targets, goal2.targets);
  });
};

/**
 * Performs a deep equality check between two arrays of targets.
 * Compares all properties of each target including name, description, mastery criteria, and discriminative stimulus.
 * @param targets1 - First array of targets to compare
 * @param targets2 - Second array of targets to compare
 * @returns true if all targets and their properties are equal, false otherwise
 */
const areTargetsEqual = (targets1: TargetItem[], targets2: TargetItem[]): boolean => {
  if (targets1 === targets2) return true;
  if (targets1.length !== targets2.length) return false;

  return targets1.every((target1, i) => {
    const target2 = targets2[i];
    if (!target2) return false;

    return target1.targetName === target2.targetName && target1.targetDescription === target2.targetDescription;
  });
};
